import React, { useEffect, useMemo, useState } from "react";
import {
  SponsorAgreementResponseDto,
  SponsorAgreementAdvertisementResponseDto,
  CreateSponsorAgreementRequestDto,
  UpdateSponsorAgreementRequestDto,
  AgreementStatus,
  SponsorMandantType,
} from "@/lib/api/interfaces/sponsor-agreements.interface";
import {
  useSponsorAgreementCreate,
  useSponsorAgreementUpdate,
  useSponsorAgreementAdvertisementsQuery,
  useSponsorAgreementDocumentsUpload,
} from "@/lib/api/requests/sponsor-agreements.requests";
import { CreateBaseDialog } from "@/util-components/dialog/base-dialogs/create-base-dialog";
import { Button } from "@/ui-components/ui/button";
import { Loader2, Coins } from "lucide-react";
import { useOrganizationsCustomerFindOneQuery } from "@/lib/api/requests/organizations.requests";
import { MultiSelect } from "@/ui-components/ui/multi-select";

interface CreateSponsorAgreementDialogProps {
  sponsorId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (agreement: SponsorAgreementResponseDto) => void;
  agreement?: SponsorAgreementResponseDto;
}

export const CreateSponsorAgreementDialog: React.FC<CreateSponsorAgreementDialogProps> = ({
  sponsorId,
  open,
  onOpenChange,
  onCreated,
  agreement,
}) => {
  const { data: organization } = useOrganizationsCustomerFindOneQuery();
  const enableMandantFeature = organization?.enableMandantFeature ?? false;

  const [selectedAdvertisementIds, setSelectedAdvertisementIds] = useState<string[]>([]);
  const [isPostable, setIsPostable] = useState(false);
  const [agreementType, setAgreementType] = useState<"DONATION" | "SPONSORING">("SPONSORING");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [duration, setDuration] = useState<string>("");
  const [volume, setVolume] = useState<string>("");
  const [status, setStatus] = useState<AgreementStatus>(AgreementStatus.PENDING);
  const [files, setFiles] = useState<File[]>([]);
  const [mandantType, setMandantType] = useState<SponsorMandantType>(SponsorMandantType.VEREIN);
  const [hasInitializedDefaultsFromAdvertisement, setHasInitializedDefaultsFromAdvertisement] = useState(false);

  const {
    data: advertisements,
    isLoading,
    isError,
    refetch,
  } = useSponsorAgreementAdvertisementsQuery(sponsorId, {
    enabled: open,
  } as any);

  const createMutation = useSponsorAgreementCreate(sponsorId);
  const updateMutation = useSponsorAgreementUpdate(sponsorId);
  const uploadDocsMutation = useSponsorAgreementDocumentsUpload(sponsorId);

  const selectedAdvertisements: SponsorAgreementAdvertisementResponseDto[] = useMemo(
    () => (advertisements || []).filter((ad) => selectedAdvertisementIds.includes(ad.advertisementId)),
    [advertisements, selectedAdvertisementIds],
  );

  const primarySelectedAdvertisement: SponsorAgreementAdvertisementResponseDto | undefined = selectedAdvertisements[0];

  const hasAnyIntervalAdvertisement = useMemo(
    () =>
      selectedAdvertisements.some(
        (ad) => ad.defaultInterval && ad.defaultDuration,
      ),
    [selectedAdvertisements],
  );

  const selectedAdvertisementTypeLabel = useMemo(() => {
    if (!primarySelectedAdvertisement) return "-";
    return primarySelectedAdvertisement.type === "DONATION" ? "Spende" : "Sponsoring";
  }, [primarySelectedAdvertisement]);

  const addInterval = (start: Date, amount: number, unit?: string | null) => {
    const result = new Date(start);
    const normalizedUnit = (unit || "DAY").toUpperCase();

    switch (normalizedUnit) {
      case "YEAR":
      case "YEARS":
        result.setFullYear(result.getFullYear() + amount);
        break;
      case "MONTH":
      case "MONTHS":
        result.setMonth(result.getMonth() + amount);
        break;
      case "WEEK":
      case "WEEKS":
        result.setDate(result.getDate() + amount * 7);
        break;
      case "DAY":
      case "DAYS":
      default:
        result.setDate(result.getDate() + amount);
        break;
    }

    return result;
  };

  const toDateInput = (value: Date) =>
    `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;

  const intervalToDays = (amount: number, unit?: string | null): number => {
    const normalizedUnit = (unit || "DAY").toUpperCase();
    switch (normalizedUnit) {
      case "YEAR":
      case "YEARS":
        return amount * 365;
      case "MONTH":
      case "MONTHS":
        return amount * 30;
      case "WEEK":
      case "WEEKS":
        return amount * 7;
      case "DAY":
      case "DAYS":
      default:
        return amount;
    }
  };

  // Wenn ein Agreement zum Bearbeiten übergeben wird, State initialisieren
  useEffect(() => {
    if (agreement) {
      const ids = agreement.advertisementIds && agreement.advertisementIds.length > 0
        ? agreement.advertisementIds : [];

      setSelectedAdvertisementIds(ids);
      setStartDate(agreement.startDate ? toDateInput(new Date(agreement.startDate)) : "");
      setEndDate(agreement.endDate ? toDateInput(new Date(agreement.endDate)) : "");
      setDuration(agreement.duration ? String(agreement.duration) : "");
      setVolume(agreement.volume ? String(agreement.volume) : "");
      setStatus(agreement.status);
      if (agreement.advertisementType) {
        setAgreementType(agreement.advertisementType);
      }
      setMandantType(agreement.mandantType ?? SponsorMandantType.VEREIN);
    } else {
      setSelectedAdvertisementIds([]);
      setMandantType(SponsorMandantType.VEREIN);
    }
  }, [agreement]);

  // Defaults setzen, wenn beim Erstellen eine Auswahl getroffen wird
  useEffect(() => {
    if (!primarySelectedAdvertisement || agreement) return;

    // Nur bei der allerersten Auswahl die Defaults aus der Leistung übernehmen
    if (hasInitializedDefaultsFromAdvertisement) return;

    setIsPostable(primarySelectedAdvertisement.show_on_social_media ?? false);

    const today = new Date();
    const nextStartDate = toDateInput(today);
    setStartDate(nextStartDate);

    const hasInterval = !!primarySelectedAdvertisement.defaultInterval && !!primarySelectedAdvertisement.defaultDuration;

    if (!hasInterval) {
      setEndDate("");
      setDuration("");
    } else {
      const rawDur = Number(primarySelectedAdvertisement.defaultDuration) || 0;
      const daysDur = intervalToDays(rawDur, primarySelectedAdvertisement.defaultInterval);
      setDuration(daysDur ? String(daysDur) : "");

      if (daysDur > 0) {
        const calculatedEnd = addInterval(
          today,
          daysDur,
          "DAY",
        );
        const nextEndDate = toDateInput(calculatedEnd);
        setEndDate(nextEndDate);
      } else {
        setEndDate("");
      }
    }

    setVolume(primarySelectedAdvertisement.defaultVolume?.toString() ?? "");
    setAgreementType(primarySelectedAdvertisement.type);
    setHasInitializedDefaultsFromAdvertisement(true);
  }, [primarySelectedAdvertisement, agreement, hasInitializedDefaultsFromAdvertisement]);

  const isOneTime = !hasAnyIntervalAdvertisement;

  // Enddatum automatisch aktualisieren, wenn Startdatum ODER Duration geändert wird
  useEffect(() => {
    if (!startDate) {
      return;
    }

    const dur = Number(duration);
    if (!Number.isFinite(dur) || dur <= 0) {
      setEndDate("");
      return;
    }

    const baseStart = new Date(startDate);
    if (isNaN(baseStart.getTime())) return;

    const calculatedEnd = addInterval(
      baseStart,
      dur,
      "DAY",
    );
    const nextEndDate = toDateInput(calculatedEnd);
    setEndDate(nextEndDate);
  }, [startDate, duration]);

  const resetState = () => {
    setSelectedAdvertisementIds([]);
    setStartDate("");
    setEndDate("");
    setDuration("");
    setVolume("");
    setAgreementType("SPONSORING");
    setFiles([]);
    setMandantType(SponsorMandantType.VEREIN);
    setHasInitializedDefaultsFromAdvertisement(false);
    createMutation.reset();
    uploadDocsMutation.reset();
  };

  const handleClose = () => {
    if (createMutation.isPending) return;
    resetState();
    onOpenChange(false);
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    const isOneTimeLocal = !hasAnyIntervalAdvertisement;

    if (selectedAdvertisementIds.length === 0 || !startDate || (!isOneTimeLocal && !endDate)) return;

    const payload: any = {
      advertisementIds: selectedAdvertisementIds,
      // Legacy-Einzel-Feld zur Sicherheit für alte Backends/Analysetools
      advertisementId: selectedAdvertisementIds[0],
      startDate: new Date(startDate).toISOString(),
      isPostable: isPostable,
      status: status,
      type: agreementType,
      mandantType,
    };

    if (endDate && !isOneTimeLocal) {
      payload.endDate = new Date(endDate).toISOString();
    }
    if (duration) {
      payload.duration = Number(duration);
    }
    if (volume) {
      payload.volume = Number(volume);
    }

    try {
      let result: SponsorAgreementResponseDto;
      if (agreement) {
        result = await updateMutation.mutateAsync({
          agreementId: agreement._id,
          data: payload as UpdateSponsorAgreementRequestDto,
        });
      } else {
        result = await createMutation.mutateAsync(payload as CreateSponsorAgreementRequestDto);
      }

      // Nach dem Erstellen/Update ggf. Dokumente hochladen
      if (result?._id && files.length > 0) {
        await uploadDocsMutation.mutateAsync({ agreementId: result._id, files });
      }
      onCreated?.(result);
      resetState();
      onOpenChange(false);
    } catch {
      // Fehleranzeige im UI
    }
  };

  const isBusy = createMutation.isPending || updateMutation.isPending || uploadDocsMutation.isPending;
  const canSubmit = selectedAdvertisementIds.length > 0 && !!startDate && (isOneTime || !!endDate) && !isBusy;

  return (
    <CreateBaseDialog
      open={open}
      onClose={(refetch) => {
        if (!isBusy) {
          if (refetch) {
            // Nothing to refetch here specifically as onCreated handles it
          }
          handleClose();
        }
      }}
      icon={Coins}
      title={agreement ? "Vereinbarung bearbeiten" : "Neue Vereinbarung anlegen"}
      description={
        agreement
          ? "Passe die Vertragsdaten der bestehenden Vereinbarung an."
          : "Wähle eine Werbeleistung aus und passe anschließend die Vertragsdaten an."
      }
      isSaving={isBusy}
      canSave={canSubmit}
      formId="create-agreement-form"
      submitLabel={agreement ? "Änderungen speichern" : "Agreement anlegen"}
      submitLoadingLabel={agreement ? "Speichern..." : "Erstellen..."}
      maxWidth="xl"
    >
      <form
        id="create-agreement-form"
        onSubmit={handleSubmit}
        className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6"
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">
              Leistung
            </label>
            {isLoading ? (
              <div className="flex items-center text-sm text-gray-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Leistungen werden geladen…
              </div>
            ) : isError ? (
              <div className="space-y-2 text-sm text-red-700">
                <div>Leistungen konnten nicht geladen werden.</div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                >
                  Erneut versuchen
                </Button>
              </div>
            ) : !advertisements || advertisements.length === 0 ? (
              <div className="text-sm text-gray-500">
                Es sind keine Leistungen (Advertisements) für Agreements verfügbar.
              </div>
            ) : agreement ? (
              <div className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                {selectedAdvertisements.map((ad) => (
                  <div key={ad.advertisementId} className="flex items-center">
                    {ad.advertisementName}
                    <span className="ml-2 text-[11px] text-gray-500">
                      {ad.type === "DONATION" ? "(Spende)" : "(Sponsoring)"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <MultiSelect
                options={(advertisements || []).map((ad) => ({
                  label: `${ad.advertisementName} ${ad.type === "DONATION" ? "(Spende)" : "(Sponsoring)"}`,
                  value: ad.advertisementId,
                }))}
                value={selectedAdvertisementIds}
                onValueChange={(values) => setSelectedAdvertisementIds(values)}
                placeholder="Leistungen auswählen"
                className="mt-1"
              />
            )}
            {selectedAdvertisements.length > 0 && (
              <p className="mt-1 text-[11px] text-gray-500">
                Art der Leistung: <span className="font-medium">{selectedAdvertisementTypeLabel}</span>
                {agreement?.advertisementType && (
                  <span className="ml-1 text-[10px] text-gray-400">
                    (im Agreement aktuell gespeichert als{" "}
                    {agreement.advertisementType === "DONATION" ? "Spende" : "Sponsoring"}
                    )
                  </span>
                )}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-gray-700">
                Startdatum
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700">
                Enddatum {!isOneTime && <span className="text-red-500">*</span>}
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required={!isOneTime}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20 disabled:bg-gray-50 disabled:text-gray-500"
                disabled={isOneTime}
              />
              {isOneTime && (
                <p className="mt-1 text-[10px] text-gray-400">
                  Bei Einmalzahlungen nicht erforderlich.
                </p>
              )}
            </div>
            {!isOneTime && (
              <div>
                <label className="block text-xs font-medium text-gray-700">
                  Laufzeit (Tage)
                </label>
                <input
                  type="number"
                  min={0}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20"
                />
              </div>
            )}
            <div className={isOneTime ? "sm:col-span-2" : ""}>
              <label className="block text-xs font-medium text-gray-700">
                Betrag in €
              </label>
              <input
                type="number"
                min={0}
                value={volume}
                onChange={(e) => setVolume(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20"
              />
            </div>
          </div>

          <div className="mt-1 flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <div>
              <span className="block text-xs font-medium text-gray-700">Art des Agreements</span>
              <span className="block text-[11px] text-gray-500">
                Unabhängig von der Leistung: hier legst du fest, ob dieses Agreement als Spende oder Sponsoring gilt.
              </span>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-gray-600">
              <button
                type="button"
                onClick={() => setAgreementType("SPONSORING")}
                className={`px-2 py-0.5 rounded-full border ${
                  agreementType === "SPONSORING"
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-gray-300 bg-white"
                }`}
              >
                Sponsoring
              </button>
              <button
                type="button"
                onClick={() => setAgreementType("DONATION")}
                className={`px-2 py-0.5 rounded-full border ${
                  agreementType === "DONATION"
                    ? "border-green-500 bg-green-50 text-green-700"
                    : "border-gray-300 bg-white"
                }`}
              >
                Spende
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as AgreementStatus)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20"
              required
            >
              <option value={AgreementStatus.PENDING}>Ausstehend</option>
              <option value={AgreementStatus.ACCEPTED}>Akzeptiert</option>
              <option value={AgreementStatus.REJECTED}>Abgelehnt</option>
            </select>
          </div>

          {enableMandantFeature && (
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-700">
                Mandantentyp
              </label>
              <select
                value={mandantType}
                onChange={(e) => setMandantType(e.target.value as SponsorMandantType)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20"
                required
              >
                <option value={SponsorMandantType.VEREIN}>Verein</option>
                <option value={SponsorMandantType.SPIELBETRIEBS_GMBH}>Spielbetriebs GmbH</option>
                <option value={SponsorMandantType.FOERDERVEREIN}>Förderverein</option>
              </select>
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">
              Vertragsdokumente (optional)
            </label>
            <input
              type="file"
              multiple
              accept="application/pdf,image/*"
              onChange={(e) => setFiles(e.target.files ? Array.from(e.target.files) : [])}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20"
            />
            {files.length > 0 && (
              <div className="text-xs text-gray-500">{files.length} Datei(en) ausgewählt</div>
            )}
          </div>

          {(createMutation.isError || uploadDocsMutation.isError) && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {createMutation.isError
                ? "Agreement konnte nicht erstellt werden."
                : "Dokumente konnten nicht hochgeladen werden."}
            </div>
          )}
        </div>
      </form>
    </CreateBaseDialog>
  );
};

